import React, { useState } from 'react';
import {
  Palette,
  Layout,
  Eye,
  Save,
  RefreshCw,
  Smartphone,
  Monitor,
  Tablet,
  Settings,
  Download,
  Upload
} from 'lucide-react';

// Market Card Layout Components
import MarketCardLayout1 from './components/MarketCardLayout1';
import MarketCardLayout2 from './components/MarketCardLayout2';
import MarketCardLayout3 from './components/MarketCardLayout3';
import MarketCardLayout4 from './components/MarketCardLayout4';
import MarketCardLayout5 from './components/MarketCardLayout5';
import MarketCardLayout6 from './components/MarketCardLayout6';

// Footer Navigation Layout Components
import FooterNavLayout1 from './components/FooterNavLayout1';
import FooterNavLayout2 from './components/FooterNavLayout2';
import FooterNavLayout3 from './components/FooterNavLayout3';
import FooterNavLayout4 from './components/FooterNavLayout4';
import FooterNavLayout5 from './components/FooterNavLayout5';
import FooterNavLayout6 from './components/FooterNavLayout6';

// Button Configuration Component
import ButtonConfiguration from './components/ButtonConfiguration';

// Color Picker Component
import ColorPicker from './components/ColorPicker';

// Preview Component
import PreviewPanel from './components/PreviewPanel';
import SplashPreview from './components/SplashPreview';
import LogoPreview from './components/LogoPreview';

interface CustomizationState {
  logo: {
    url: string | null;
    primary: string;
    secondary: string;
    fontFamily: string;
    fontSize: number;
    heading: string;
    subtext: string;
  };
  splashScreen: {
    imageUrl: string | null;
  };
  // Market Card Settings
  marketCardLayout: number;
  marketCardColors: {
    background: string;
    text: string;
    button: string;
    buttonText: string;
    status: string;
    border: string;
  };

  // Footer Navigation Settings
  footerNavLayout: number;
  footerNavColors: {
    background: string;
    text: string;
    activeBackground: string;
    activeText: string;
    icon: string;
  };

  // Button Settings
  buttonConfig: {
    count: number;
    theme: string;
    layout: string;
    colors: {
      primary: string;
      secondary: string;
      text: string;
    };
  };

  // General Settings
  devicePreview: 'mobile' | 'tablet' | 'desktop';
}

const AppCustomization: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'logo' | 'splash' | 'market' | 'footer' | 'buttons' | 'preview'>('logo');
  const [customization, setCustomization] = useState<CustomizationState>({
    logo: {
      url: null,
      primary: '#1f6feb',
      secondary: '#0ea5e9',
      fontFamily: 'Inter',
      fontSize: 14,
      heading: 'Welcome back!',
      subtext: "Hey, just log in to keep going!",
    },
    splashScreen: {
      imageUrl: localStorage.getItem('splashScreenUrl') || null,
    },
    marketCardLayout: 1,
    marketCardColors: {
      background: '#ffffff',
      text: '#000000',
      button: '#3b82f6',
      buttonText: '#ffffff',
      status: '#10b981',
      border: '#e5e7eb'
    },
    footerNavLayout: 1,
    footerNavColors: {
      background: '#1f2937',
      text: '#ffffff',
      activeBackground: '#3b82f6',
      activeText: '#ffffff',
      icon: '#ffffff'
    },
    buttonConfig: {
      count: 2,
      theme: 'gradient',
      layout: 'horizontal',
      colors: {
        primary: '#3b82f6',
        secondary: '#10b981',
        text: '#ffffff'
      }
    },
    devicePreview: 'mobile'
  });

  const marketCardLayouts = [
    { id: 1, name: 'Standard Blue Theme Card', component: MarketCardLayout1 },
    { id: 2, name: 'Green Theme - MarketCardLayout2', component: MarketCardLayout2 },
    { id: 3, name: 'Gradient Theme - MarketCardLayout3', component: MarketCardLayout3 },
    { id: 4, name: 'Red Theme - MarketCardLayout4', component: MarketCardLayout4 },
    { id: 5, name: 'Blue Card with Red Banner', component: MarketCardLayout5 },
    { id: 6, name: 'Dark Blue Theme - MarketCardLayout6', component: MarketCardLayout6 }
  ];

  const footerNavLayouts = [
    { id: 1, name: 'Dark Gray with Blue Active', component: FooterNavLayout1 },
    { id: 2, name: 'Solid Green Rectangular', component: FooterNavLayout2 },
    { id: 3, name: 'White with Purple Active Text', component: FooterNavLayout3 },
    { id: 4, name: 'Red with Raised Active State', component: FooterNavLayout4 },
    { id: 5, name: 'White Rounded with Blue Circle', component: FooterNavLayout5 },
    { id: 6, name: 'Dark Gray with Green Circle', component: FooterNavLayout6 }
  ];

  const updateCustomization = (section: keyof CustomizationState, updates: any) => {
    setCustomization(prev => {
      const current = prev[section] as any;
      // If the current section is an object and updates is an object, merge; otherwise set directly
      const nextValue = (current !== null && typeof current === 'object' && typeof updates === 'object')
        ? { ...current, ...updates }
        : updates;
      return { ...prev, [section]: nextValue } as CustomizationState;
    });
  };

  const handleSplashUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      updateCustomization('splashScreen', { imageUrl: url });
      localStorage.setItem('splashScreenUrl', url);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      updateCustomization('logo', { url });
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    // Save customization settings
    localStorage.setItem('appCustomization', JSON.stringify(customization));
    console.log('Customization saved:', customization);
  };

  const handleReset = () => {
    setCustomization({
      logo: {
        url: null,
        primary: '#1f6feb',
        secondary: '#0ea5e9',
        fontFamily: 'Inter',
        fontSize: 14,
        heading: 'Welcome back!',
        subtext: "Hey, just log in to keep going!",
      },
      splashScreen: {
        imageUrl: null,
      },
      marketCardLayout: 1,
      marketCardColors: {
        background: '#ffffff',
        text: '#000000',
        button: '#3b82f6',
        buttonText: '#ffffff',
        status: '#10b981',
        border: '#e5e7eb'
      },
      footerNavLayout: 1,
      footerNavColors: {
        background: '#1f2937',
        text: '#ffffff',
        activeBackground: '#3b82f6',
        activeText: '#ffffff',
        icon: '#ffffff'
      },
      buttonConfig: {
        count: 2,
        theme: 'gradient',
        layout: 'horizontal',
        colors: {
          primary: '#3b82f6',
          secondary: '#10b981',
          text: '#ffffff'
        }
      },
      devicePreview: 'mobile'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">App Customization</h1>
          <p className="text-gray-600 dark:text-gray-400">Customize your app's appearance and layout</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>

      {/* Device Preview Toggle */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm font-medium text-gray-700">Preview Device:</span>
        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => updateCustomization('devicePreview', 'mobile')}
            className={`p-2 ${customization.devicePreview === 'mobile' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateCustomization('devicePreview', 'tablet')}
            className={`p-2 ${customization.devicePreview === 'tablet' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateCustomization('devicePreview', 'desktop')}
            className={`p-2 ${customization.devicePreview === 'desktop' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
          >
            <Monitor className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Left Panel - Customization Controls */}
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            {[
              { id: 'logo', label: 'Logo', icon: Upload },
              { id: 'splash', label: 'Splash Screen', icon: Upload },
              { id: 'market', label: 'Market Cards', icon: Layout },
              { id: 'footer', label: 'Footer Nav', icon: Settings },
              { id: 'buttons', label: 'Buttons', icon: Palette },
              { id: 'preview', label: 'Preview', icon: Eye }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'logo' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Logo</h3>
                <p className="text-sm text-gray-600">Use a JPG, PNG, or SVG smaller than 100kb. 50px by 50px works best.</p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center gap-3 bg-gray-50">
                  <div className="text-gray-500 text-sm">Drag Files here or</div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Browse
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                  </label>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900 mb-1">Choose Primary Colors</div>
                    <p className="text-xs text-gray-500 mb-2">Hey, just log in to keep going!</p>
                    <ColorPicker label="Primary" value={customization.logo.primary} onChange={(c) => updateCustomization('logo', { primary: c })} />
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-900 mb-1">Choose Secondary Colors</div>
                    <p className="text-xs text-gray-500 mb-2">Hey, just log in to keep going!</p>
                    <ColorPicker label="Secondary" value={customization.logo.secondary} onChange={(c) => updateCustomization('logo', { secondary: c })} />
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-900 mb-1">Choose Fonts</div>
                    <p className="text-xs text-gray-500 mb-2">Hey, just log in to keep going!</p>
                    <div className="flex items-center gap-2">
                      <select value={customization.logo.fontFamily} onChange={(e) => updateCustomization('logo', { fontFamily: e.target.value })} className="border rounded px-2 py-1 text-xs">
                        {['Inter', 'Arial', 'Roboto', 'Poppins', 'Nunito'].map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                      <select value={customization.logo.fontSize} onChange={(e) => updateCustomization('logo', { fontSize: parseInt(e.target.value) })} className="border rounded px-2 py-1 text-xs">
                        {[12, 14, 16, 18].map(s => (
                          <option key={s} value={s}>{s} px</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-900 mb-1">Texts</div>
                    <p className="text-xs text-gray-500 mb-2">Customize the heading and subtitle</p>
                    <div className="space-y-2">
                      <input value={customization.logo.heading} onChange={(e) => updateCustomization('logo', { heading: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded" placeholder="Welcome back!" />
                      <input value={customization.logo.subtext} onChange={(e) => updateCustomization('logo', { subtext: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded" placeholder="Hey Happy User" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'splash' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Upload splash screen</h3>
                <p className="text-sm text-gray-600">Hey, just log in to keep going!</p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center gap-3 bg-gray-50">
                  <div className="text-gray-500 text-sm">Upload Splash Screen</div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Browse
                    <input type="file" accept="image/*" className="hidden" onChange={handleSplashUpload} />
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'market' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Market Card Layouts</h3>

                {/* Layout Selection */}
                <div className="grid grid-cols-2 gap-4">
                  {marketCardLayouts.map((layout) => (
                    <button
                      key={layout.id}
                      onClick={() => updateCustomization('marketCardLayout', layout.id)}
                      className={`p-4 border-2 rounded-lg text-left transition-colors ${customization.marketCardLayout === layout.id
                          ? 'border-blue-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="text-sm font-medium text-gray-900">{layout.name}</div>
                      <div className="mt-2 text-xs text-gray-500">Layout {layout.id}</div>
                    </button>
                  ))}
                </div>

                {/* Color Customization */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Color Customization</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <ColorPicker
                      label="Background"
                      value={customization.marketCardColors.background}
                      onChange={(color: string) => updateCustomization('marketCardColors', { background: color })}
                    />
                    <ColorPicker
                      label="Text"
                      value={customization.marketCardColors.text}
                      onChange={(color: string) => updateCustomization('marketCardColors', { text: color })}
                    />
                    <ColorPicker
                      label="Button"
                      value={customization.marketCardColors.button}
                      onChange={(color: string) => updateCustomization('marketCardColors', { button: color })}
                    />
                    <ColorPicker
                      label="Button Text"
                      value={customization.marketCardColors.buttonText}
                      onChange={(color: string) => updateCustomization('marketCardColors', { buttonText: color })}
                    />
                    <ColorPicker
                      label="Status"
                      value={customization.marketCardColors.status}
                      onChange={(color: string) => updateCustomization('marketCardColors', { status: color })}
                    />
                    <ColorPicker
                      label="Border"
                      value={customization.marketCardColors.border}
                      onChange={(color: string) => updateCustomization('marketCardColors', { border: color })}
                    />
                  </div>
                </div>

                {/* Choose Fonts (placed below Color Customization) */}
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900">Choose Fonts</div>
                  <p className="text-xs text-gray-500 mb-1">Select the font and size to apply to previews.</p>
                  <div className="flex items-center gap-2">
                    <select
                      value={customization.logo.fontFamily}
                      onChange={(e) => updateCustomization('logo', { fontFamily: e.target.value })}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {['Inter', 'Arial', 'Roboto', 'Poppins', 'Nunito'].map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <select
                      value={customization.logo.fontSize}
                      onChange={(e) => updateCustomization('logo', { fontSize: parseInt(e.target.value) })}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {[12, 14, 16, 18].map(s => (
                        <option key={s} value={s}>{s} px</option>
                      ))}
                    </select>
                  </div>
                </div>

              </div>
            )}

            {activeTab === 'footer' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Footer Navigation Layouts</h3>

                {/* Layout Selection */}
                <div className="grid grid-cols-2 gap-4">
                  {footerNavLayouts.map((layout) => (
                    <button
                      key={layout.id}
                      onClick={() => updateCustomization('footerNavLayout', layout.id)}
                      className={`p-4 border-2 rounded-lg text-left transition-colors ${customization.footerNavLayout === layout.id
                          ? 'border-blue-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="text-sm font-medium text-gray-900">{layout.name}</div>
                      <div className="mt-2 text-xs text-gray-500">Layout {layout.id}</div>
                    </button>
                  ))}
                </div>

                {/* Color Customization */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Color Customization</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <ColorPicker
                      label="Background"
                      value={customization.footerNavColors.background}
                      onChange={(color: string) => updateCustomization('footerNavColors', { background: color })}
                    />
                    <ColorPicker
                      label="Text"
                      value={customization.footerNavColors.text}
                      onChange={(color: string) => updateCustomization('footerNavColors', { text: color })}
                    />
                    <ColorPicker
                      label="Active Background"
                      value={customization.footerNavColors.activeBackground}
                      onChange={(color: string) => updateCustomization('footerNavColors', { activeBackground: color })}
                    />
                    <ColorPicker
                      label="Active Text"
                      value={customization.footerNavColors.activeText}
                      onChange={(color: string) => updateCustomization('footerNavColors', { activeText: color })}
                    />
                    <ColorPicker
                      label="Icon"
                      value={customization.footerNavColors.icon}
                      onChange={(color: string) => updateCustomization('footerNavColors', { icon: color })}
                    />
                  </div>
                </div>

                {/* Choose Fonts (Footer Nav) */}
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900">Choose Fonts</div>
                  <p className="text-xs text-gray-500 mb-1">Select the font and size to apply to previews.</p>
                  <div className="flex items-center gap-2">
                    <select
                      value={customization.logo.fontFamily}
                      onChange={(e) => updateCustomization('logo', { fontFamily: e.target.value })}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {['Inter', 'Arial', 'Roboto', 'Poppins', 'Nunito'].map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <select
                      value={customization.logo.fontSize}
                      onChange={(e) => updateCustomization('logo', { fontSize: parseInt(e.target.value) })}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {[12, 14, 16, 18].map(s => (
                        <option key={s} value={s}>{s} px</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'buttons' && (
              <ButtonConfiguration
                config={customization.buttonConfig}
                onChange={(updates) => updateCustomization('buttonConfig', updates)}
              />
            )}

            {activeTab === 'preview' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
                <p className="text-sm text-gray-600">Preview your customizations in real-time</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="lg:sticky lg:top-6 lg:h-screen lg:overflow-y-auto">
          {activeTab === 'logo' ? (
            <LogoPreview
              logoUrl={customization.logo.url}
              primaryColor={customization.logo.primary}
              secondaryColor={customization.logo.secondary}
              fontFamily={customization.logo.fontFamily}
              fontSize={customization.logo.fontSize}
              heading={customization.logo.heading}
              subtext={customization.logo.subtext}
            />
          ) : activeTab === 'splash' ? (
            <SplashPreview imageUrl={customization.splashScreen.imageUrl} device={customization.devicePreview} />
          ) : (
            <PreviewPanel
              customization={customization}
              marketCardLayouts={marketCardLayouts}
              footerNavLayouts={footerNavLayouts}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AppCustomization;
